import {
  CollapsedWarningPill,
  CollapsibleWarningBanner,
} from "@/components/navigation/CollapsibleWarningBanner";
import {
  EngineImageOverride,
  useHasEngineImageOverride,
} from "@/utils/hooks/engine/use-has-engine-image-override";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { useMemo, useState } from "react";

const MAX_INITIAL_ITEMS = 1;

export interface EngineImageOverrideWarningProps {
  collapsed?: boolean;
  onToggleCollapsed?: (collapsed: boolean) => void;
}

/**
 * Warning banner displayed in the navigation bar when the environment has
 * an engine image override or pinned base image.
 * Only visible to Chalk employees (checked within the hook).
 */
export const EngineImageOverrideWarning: React.FC<EngineImageOverrideWarningProps> = ({
  collapsed = false,
  onToggleCollapsed,
}) => {
  const { hasOverride, overrides } = useHasEngineImageOverride();
  const { projectId, environmentId } = useRouteInfo();
  const [showAll, setShowAll] = useState(false);

  const defaultItems = useMemo(() => {
    return overrides.slice(0, MAX_INITIAL_ITEMS);
  }, [overrides]);

  const restItems = useMemo(() => {
    return overrides.slice(MAX_INITIAL_ITEMS);
  }, [overrides]);

  if (!hasOverride) {
    return null;
  }

  if (collapsed) {
    return (
      <CollapsedWarningPill
        className="border-cds-status-red-border bg-cds-status-red-fill text-cds-status-red-text hover:brightness-95 dark:border-cds-status-red-border-dark dark:bg-cds-status-red-fill-dark dark:text-cds-status-red-text-dark dark:hover:brightness-110"
        label="Engine overrides"
        onClick={() => onToggleCollapsed?.(false)}
      />
    );
  }

  const getResourceLink = (resourceGroup: string, serviceKind: string) => {
    if (!projectId || !environmentId) {
      return undefined;
    }

    return Routes.project.environment.resourcesResourceGroupService(
      projectId,
      environmentId,
      resourceGroup,
      serviceKind,
    );
  };

  const renderOverrideItem = (override: EngineImageOverride, idx: number) => {
    const link =
      override.resourceGroup && override.serviceKind
        ? getResourceLink(override.resourceGroup, override.serviceKind)
        : undefined;
    const content = (
      <>
        - <span className={"font-medium"}>{override.displayKey}:</span>{" "}
        <span>{override.imageUrl}</span>
      </>
    );

    return (
      <li key={`${override.source}-${override.displayKey}-${idx}`}>
        {link ? (
          <ChalkLink href={link} className="hover:underline">
            {content}
          </ChalkLink>
        ) : (
          content
        )}
      </li>
    );
  };

  return (
    <CollapsibleWarningBanner
      className="bg-cds-status-red-fill text-cds-status-red-text dark:bg-cds-status-red-fill-dark dark:text-cds-status-red-text-dark"
      onCollapse={onToggleCollapsed ? () => onToggleCollapsed(true) : undefined}
      title={
        <>
          NOTE: This environment has an overridden or pinned base image. This will cause
          hard-to-debug behaviors.
        </>
      }
    >
      {overrides.length > 0 && (
        <ul className="mt-1 text-xs space-y-0.5">
          {defaultItems.map((override, idx) => renderOverrideItem(override, idx))}
          {showAll &&
            restItems.map((override, idx) => renderOverrideItem(override, idx + MAX_INITIAL_ITEMS))}
          {restItems.length > 0 && (
            <li>
              <button
                onClick={() => setShowAll(!showAll)}
                className="hover:underline cursor-pointer"
              >
                {showAll ? `- Show less` : `+ ${restItems.length} more`}
              </button>
            </li>
          )}
        </ul>
      )}
    </CollapsibleWarningBanner>
  );
};
